(() => {
  'use strict';
  const searches = new WeakMap(), dialogs = new WeakMap();
  const within = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector),
  ];
  const plain = (event) => !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  const focus = (node) => { if (node?.isConnected) node.focus({ preventScroll: true }); };
  function buildPredictiveURL(endpoint, query, base) {
    try {
      const page = new URL(base), url = new URL(endpoint || '', page);
      if (!endpoint || !['http:', 'https:'].includes(url.protocol) || url.origin !== page.origin || url.username || url.password) return null;
      url.hash = '';
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,article,page');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('section_id', 'ziqing-predictive-search');
      return url.href;
    } catch (_) { return null; }
  }
  function initSearch(root, view) {
    if (searches.has(root)) return searches.get(root);
    const own = (selector) => [...root.querySelectorAll(selector)].filter((node) => node.closest('[data-zsearch]') === root);
    const form = own('form[data-zsearch-form]')[0], input = form?.querySelector('input[name="q"]');
    const clear = own('[data-zsearch-clear]')[0], predictions = own('[data-zsearch-predictions]')[0], status = own('[data-zsearch-status]')[0];
    if (!form || !input || !clear || clear.tagName !== 'BUTTON' || clear.type !== 'button' || !predictions || !status
      || typeof view.fetch !== 'function' || typeof view.AbortController !== 'function' || typeof view.DOMParser !== 'function'
      || !buildPredictiveURL(root.dataset.predictiveUrl, '', view.location.href)) return null;
    const doc = root.ownerDocument, dialog = root.closest('dialog[data-zsearch-dialog]');
    // Shopify renders these plain strings per component and active locale.
    // Missing translations must not disable the native full-search fallback.
    const messages = {
      ready: root.dataset.i18nReady || 'Search suggestions updated. Submit to see all results.',
      empty: root.dataset.i18nEmpty || 'No suggestions found. Submit to search all results.',
      loading: root.dataset.i18nLoading || 'Loading search suggestions…',
      unavailable: root.dataset.i18nUnavailable || 'Suggestions are unavailable. Submit your search to see all results.',
    };
    const removers = [];
    const listen = (target, type, fn) => { target.addEventListener(type, fn); removers.push(() => target.removeEventListener(type, fn)); };
    const originalControls = input.getAttribute('aria-controls');
    if (originalControls === null && predictions.id && doc.getElementById(predictions.id) === predictions) input.setAttribute('aria-controls', predictions.id);
    let stopped = false, composing = false, suppressFocusCache = false, timer = null, blurTimer = null, requestTimer = null, request = null, serial = 0, cachedQuery = null;
    const visible = () => root.isConnected && !root.closest('[hidden]') && (!dialog || dialog.open);
    const eligible = () => !composing && !input.disabled && visible() && Array.from(input.value.trim()).length >= 2;
    const links = () => [...predictions.querySelectorAll('a[href]')].filter((link) => link.tabIndex >= 0 && !link.closest('[hidden],[aria-hidden="true"]'));
    const state = (value, message = '') => {
      root.dataset.zsearchState = value;
      predictions.setAttribute('aria-busy', String(value === 'loading'));
      status.textContent = message;
    };
    const stopRequest = () => {
      serial += 1;
      if (timer !== null) view.clearTimeout(timer);
      if (requestTimer !== null) view.clearTimeout(requestTimer);
      requestTimer = null;
      timer = null; request?.abort(); request = null;
    };
    const dismiss = (focusInput = false) => {
      stopRequest(); predictions.hidden = true; state('idle');
      if (focusInput) {
        suppressFocusCache = true;
        try { focus(input); } finally { suppressFocusCache = false; }
      }
    };
    const forget = () => { cachedQuery = null; predictions.replaceChildren(); };
    const showCached = () => {
      if (!eligible() || cachedQuery !== input.value) return false;
      predictions.hidden = false;
      state('ready', links().length ? messages.ready : messages.empty);
      return true;
    };
    const current = (ticket, query) => !stopped && ticket === serial && query === input.value && eligible() && root.contains(doc.activeElement);
    async function fetchSuggestions() {
      timer = null;
      if (!eligible() || !root.contains(doc.activeElement)) return;
      const query = input.value, ticket = ++serial;
      const url = buildPredictiveURL(root.dataset.predictiveUrl, query, view.location.href);
      if (!url) { dismiss(); return; }
      const controller = new view.AbortController();
      request = controller;
      state('loading', messages.loading);
      const timeout = view.setTimeout(() => {
        if (stopped || ticket !== serial || request !== controller) return;
        const report = current(ticket, query);
        controller.abort(); request = null; requestTimer = null; serial += 1;
        predictions.hidden = true; forget();
        state(report ? 'error' : 'idle', report ? messages.unavailable : '');
      }, 8000);
      requestTimer = timeout;
      try {
        const response = await view.fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Prediction request failed');
        const html = await response.text();
        if (!current(ticket, query)) return;
        const parsed = new view.DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector('[data-zsearch-response]');
        if (!source) throw new Error('Prediction section missing');
        // Insert only this same-origin section. Executable elements/attributes and
        // unsafe navigation are never part of the ordinary suggestions list.
        source.querySelectorAll('script,style,iframe,object,embed,form,input,base,meta,link').forEach((node) => node.remove());
        source.querySelectorAll('button').forEach((button) => {
          if (!button.matches('button.zsearch-all[type="submit"]')) { button.remove(); return; }
          // The all-results action belongs to the EXISTING outer GET form.
          // Never import an alternate form, destination/method or successful q.
          [...button.attributes].forEach((attribute) => { if (!['class', 'type', 'aria-label', 'title'].includes(attribute.name)) button.removeAttribute(attribute.name); });
        });
        source.querySelectorAll('*').forEach((node) => {
          [...node.attributes].forEach((attribute) => { if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name); });
          if (node.hasAttribute('href')) {
            try {
              const link = new URL(node.getAttribute('href'), view.location.href);
              if (!['http:', 'https:'].includes(link.protocol) || link.origin !== view.location.origin || link.username || link.password) node.removeAttribute('href');
            } catch (_) { node.removeAttribute('href'); }
          }
        });
        predictions.replaceChildren(...[...source.childNodes].map((node) => doc.importNode(node, true)));
        cachedQuery = query; request = null; showCached();
      } catch (error) {
        if (!current(ticket, query)) return;
        request = null; predictions.hidden = true; forget();
        state('error', messages.unavailable);
      } finally {
        view.clearTimeout(timeout);
        if (requestTimer === timeout) requestTimer = null;
      }
    }
    const queue = () => {
      dismiss(); clear.hidden = input.value.length === 0;
      if (!eligible()) { forget(); return; }
      if (showCached()) return;
      forget(); timer = view.setTimeout(fetchSuggestions, 300);
    };
    listen(input, 'input', (event) => {
      if (event.isComposing || composing) { dismiss(); forget(); clear.hidden = input.value.length === 0; return; }
      queue();
    });
    listen(input, 'compositionstart', () => { composing = true; dismiss(); forget(); });
    listen(input, 'compositionend', () => { composing = false; queue(); });
    listen(clear, 'click', () => { input.value = ''; dismiss(); forget(); clear.hidden = true; focus(input); });
    listen(form, 'submit', () => { dismiss(); }); // Native GET, q and other successful controls stay untouched.
    listen(root, 'keydown', (event) => {
      if (!plain(event) || event.isComposing || composing || event.keyCode === 229) return;
      if (event.key === 'ArrowDown' && event.target === input && !predictions.hidden && links().length) {
        event.preventDefault(); focus(links()[0]);
      } else if (event.key === 'Escape' && (!predictions.hidden || timer !== null || request !== null)) {
        event.preventDefault(); event.stopPropagation(); dismiss(true);
      }
    });
    listen(root, 'focusin', (event) => {
      if (blurTimer !== null) view.clearTimeout(blurTimer);
      blurTimer = null;
      if (event.target === input) { clear.hidden = input.value.length === 0; if (!suppressFocusCache) showCached(); }
    });
    listen(root, 'focusout', (event) => {
      if (root.contains(event.relatedTarget)) return;
      if (event.relatedTarget) { dismiss(); return; }
      if (blurTimer !== null) view.clearTimeout(blurTimer);
      blurTimer = view.setTimeout(() => { blurTimer = null; if (!root.contains(doc.activeElement)) dismiss(); }, 0);
    });
    listen(doc, 'pointerdown', (event) => { if (!root.contains(event.target)) dismiss(); });
    const api = {
      dismiss,
      active: () => !predictions.hidden || timer !== null || request !== null,
      destroy() {
        if (stopped) return;
        stopped = true; dismiss(); forget(); removers.forEach((remove) => remove());
        if (blurTimer !== null) view.clearTimeout(blurTimer);
        clear.hidden = true; root.removeAttribute('data-zsearch-ready'); root.removeAttribute('data-zsearch-state');
        predictions.removeAttribute('aria-busy');
        if (originalControls === null) input.removeAttribute('aria-controls');
        else input.setAttribute('aria-controls', originalControls);
        searches.delete(root);
      },
    };
    clear.hidden = input.value.length === 0; predictions.hidden = true; state('idle');
    root.setAttribute('data-zsearch-ready', 'true'); searches.set(root, api);
    return api;
  }
  function initDialog(dialog, view) {
    if (dialogs.has(dialog)) return dialogs.get(dialog);
    if (dialog.tagName !== 'DIALOG' || typeof dialog.showModal !== 'function' || typeof dialog.close !== 'function') return null;
    const doc = dialog.ownerDocument, close = dialog.querySelector('[data-zsearch-close]');
    if (!close || close.tagName !== 'BUTTON' || close.type !== 'button') return null;
    const removers = [];
    const listen = (target, type, fn) => { target.addEventListener(type, fn); removers.push(() => target.removeEventListener(type, fn)); };
    let trigger = null, stopped = false, scroll = null;
    const components = () => within(dialog, '[data-zsearch]').map((root) => searches.get(root)).filter(Boolean);
    const finish = () => {
      components().forEach((api) => api.dismiss());
      doc.querySelectorAll('[data-zsearch-open]').forEach((node) => { if (node.getAttribute('aria-controls') === dialog.id) node.setAttribute('aria-expanded', 'false'); });
      if (scroll) {
        if (doc.documentElement.style.overflow === 'hidden') {
          if (scroll.overflow) doc.documentElement.style.setProperty('overflow', scroll.overflow, scroll.priority);
          else doc.documentElement.style.removeProperty('overflow');
        }
        if (view.scrollX !== scroll.x || view.scrollY !== scroll.y) view.scrollTo({ left: scroll.x, top: scroll.y, behavior: 'instant' });
        scroll = null;
      }
      const target = trigger; trigger = null; focus(target);
    };
    const api = {
      open(opener) {
        if (stopped) return false;
        if (dialog.open) { focus(dialog.querySelector('input[name="q"]')); return true; }
        const saved = { x: view.scrollX, y: view.scrollY, overflow: doc.documentElement.style.getPropertyValue('overflow'), priority: doc.documentElement.style.getPropertyPriority('overflow') };
        try { dialog.showModal(); } catch (_) { return false; }
        if (!dialog.open) return false;
        scroll = saved; trigger = opener; doc.documentElement.style.setProperty('overflow', 'hidden');
        doc.querySelectorAll('[data-zsearch-open]').forEach((node) => { if (node.getAttribute('aria-controls') === dialog.id) node.setAttribute('aria-expanded', 'true'); });
        focus(dialog.querySelector('input[name="q"]')); return true;
      },
      close() { if (dialog.open) dialog.close(); finish(); },
      destroy() { if (stopped) return; api.close(); stopped = true; removers.forEach((remove) => remove()); dialogs.delete(dialog); },
    };
    listen(close, 'click', () => api.close());
    listen(dialog, 'close', finish);
    listen(dialog, 'cancel', (event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      const active = components().filter((item) => item.active());
      if (active.length) active.forEach((item) => item.dismiss(true));
      else api.close();
    });
    listen(dialog, 'click', (event) => {
      if (event.target !== dialog || !plain(event) || event.button > 0) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) api.close();
    });
    dialogs.set(dialog, api); return api;
  }
  function install(view, doc) {
    if (view.__ziqingSearch) { view.__ziqingSearch.init(doc); return view.__ziqingSearch; }
    const init = (scope) => {
      within(scope, '[data-zsearch]').forEach((root) => initSearch(root, view));
      within(scope, 'dialog[data-zsearch-dialog]').forEach((dialog) => initDialog(dialog, view));
    };
    const unload = (scope) => {
      within(scope, 'dialog[data-zsearch-dialog]').forEach((dialog) => dialogs.get(dialog)?.destroy());
      within(scope, '[data-zsearch]').forEach((root) => searches.get(root)?.destroy());
    };
    const open = (event) => {
      if (!plain(event) || event.button > 0) return;
      const opener = event.target.closest?.('a[data-zsearch-open]');
      if (!opener || opener.hasAttribute('download') || (opener.target && opener.target !== '_self')) return;
      const dialog = doc.getElementById(opener.getAttribute('aria-controls') || '');
      if (!dialog?.matches('dialog[data-zsearch-dialog]')) return;
      const form = dialog.querySelector('form[data-zsearch-form]');
      try {
        const url = new URL(opener.href, view.location.href), action = new URL(form?.action || '', view.location.href);
        if (!form || url.origin !== view.location.origin || !['http:', 'https:'].includes(url.protocol) || url.pathname !== action.pathname) return;
      } catch (_) { return; }
      if (initDialog(dialog, view)?.open(opener)) event.preventDefault();
    };
    const loaded = (event) => init(event.target), removed = (event) => unload(event.target);
    const ready = () => init(doc);
    const api = { init, destroy() { unload(doc); doc.removeEventListener('DOMContentLoaded', ready); doc.removeEventListener('click', open); doc.removeEventListener('shopify:section:load', loaded); doc.removeEventListener('shopify:section:unload', removed); delete view.__ziqingSearch; } };
    view.__ziqingSearch = api;
    doc.addEventListener('click', open);
    doc.addEventListener('shopify:section:load', loaded);
    doc.addEventListener('shopify:section:unload', removed);
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', ready, { once: true }); else init(doc);
    return api;
  }
  if (typeof window === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) module.exports = { buildPredictiveURL, initSearch, initDialog, install };
  } else install(window, document);
})();
